import type { DepositMode, StoreSettings } from '@/lib/api/stores.js';
import type { RepairService } from '@/lib/api/repair-services.js';

const PART_CATEGORY_SLUGS = new Set(['screen', 'battery', 'charging', 'camera', 'part']);

export function serviceNeedsParts(service: Pick<RepairService, 'categorySlug'>): boolean {
  const slug = service.categorySlug;
  return Boolean(slug && PART_CATEGORY_SLUGS.has(slug));
}

export function calculateDepositAmount(
  store: Pick<StoreSettings, 'depositRequiredAmount' | 'depositRequiredPct'>,
  ticketTotal: number,
): number {
  const pct = Number(store.depositRequiredPct ?? 0);
  if (pct > 0) {
    return Math.round(((ticketTotal * pct) / 100) * 100) / 100;
  }
  return Number(store.depositRequiredAmount ?? 0);
}

export function evaluateDepositPreview(
  store: Pick<
    StoreSettings,
    | 'depositMode'
    | 'depositRequiredAmount'
    | 'depositRequiredPct'
    | 'depositRulePartsOrder'
    | 'depositRuleMinAmount'
  >,
  ticketTotal: number,
  options?: { repairsNeedParts?: boolean },
): {
  showSection: boolean;
  depositRequired: boolean;
  depositAmount: number;
  usePercentage: boolean;
  percentage: number;
} {
  const mode = (store.depositMode ?? 'optional') as DepositMode;
  const pct = Number(store.depositRequiredPct ?? 0);
  const suggested = calculateDepositAmount(store, ticketTotal);

  if (mode === 'none') {
    return {
      showSection: false,
      depositRequired: false,
      depositAmount: 0,
      usePercentage: pct > 0,
      percentage: pct,
    };
  }

  if (mode === 'required') {
    return {
      showSection: true,
      depositRequired: true,
      depositAmount: suggested,
      usePercentage: pct > 0,
      percentage: pct,
    };
  }

  if (mode === 'optional') {
    return {
      showSection: true,
      depositRequired: false,
      depositAmount: suggested,
      usePercentage: pct > 0,
      percentage: pct,
    };
  }

  let required = false;
  if (store.depositRulePartsOrder && options?.repairsNeedParts) {
    required = true;
  }
  const minRule = Number(store.depositRuleMinAmount ?? 0);
  if (minRule > 0 && ticketTotal >= minRule) {
    required = true;
  }

  return {
    showSection: required,
    depositRequired: required,
    depositAmount: required ? suggested : 0,
    usePercentage: pct > 0,
    percentage: pct,
  };
}

export function depositRemaining(
  depositAmount: string | number | null | undefined,
  depositCollected: string | number | null | undefined,
): number {
  return Math.max(
    0,
    Number(depositAmount ?? 0) - Number(depositCollected ?? 0),
  );
}

export function canManageStoreRole(role: string | undefined): boolean {
  return role === 'platform_admin' || role === 'tenant_admin' || role === 'store_manager';
}
